import { type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './components/layout/AppShell';
import { AccessPage } from './features/account-access/pages/AccessPage';
import { PlatformAnalyticsPage } from './features/analytics/pages/PlatformAnalyticsPage';
import { WrongSpaceNotice } from './features/auth/components/WrongSpaceNotice';
import { ChangePasswordPage } from './features/auth/pages/ChangePasswordPage';
import { LoginPage } from './features/auth/pages/LoginPage';
import { DashboardPage } from './features/dashboard/pages/DashboardPage';
import { MyAccountPage } from './features/my-account/pages/MyAccountPage';
import { ProvisionOrganisationPage } from './features/organisations/pages/ProvisionOrganisationPage';
import { AdministratorsPage } from './features/platform-accounts/pages/AdministratorsPage';
import { MerchantsPage } from './features/platform-admin/pages/MerchantsPage';
import { PortfoliosPage } from './features/portfolios/pages/PortfoliosPage';
import { SecurityPage } from './features/security/pages/SecurityPage';
import { useSession } from './lib/session';

/**
 * Not authenticated at all -> login. A provisional password -> the password change (the API refuses everything else until the person
 * has chosen their own). Otherwise the page: whether the account may DO anything here is the server's decision, on every request.
 */
function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const { isAuthenticated, mustChangePassword, roles } = useSession();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  // One sign-in serves both spaces: an account with no platform role (a merchant) is told so, not handed an empty shell of 403s.
  if (roles.length === 0) return <WrongSpaceNotice />;
  return children;
}

/**
 * A page reserved to the superadmin (platform administrators). A display convenience, never the barrier: a platform_admin who reached the
 * address anyway would get a 403 from every call the page makes.
 */
function RequireSuperadmin({ children }: { children: ReactElement }): ReactElement {
  const { roles } = useSession();
  if (!roles.includes('superadmin')) return <Navigate to="/merchants" replace />;
  return children;
}

/**
 * Where the console opens (lot K3): the SUPERADMIN lands on the KPI dashboard, a platform administrator on its merchants. A display
 * choice, never a barrier: the dashboard's own calls are refused (403) to anyone else.
 */
function Home(): ReactElement {
  const { roles } = useSession();
  return <Navigate to={roles.includes('superadmin') ? '/dashboard' : '/merchants'} replace />;
}

/** `/change-password` only needs a session to exist - its whole job is to lift the confinement. */
function RequireSession({ children }: { children: ReactElement }): ReactElement {
  const { isAuthenticated } = useSession();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

export function App(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/change-password"
        element={
          <RequireSession>
            <ChangePasswordPage />
          </RequireSession>
        }
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route
          path="dashboard"
          element={
            <RequireSuperadmin>
              <DashboardPage />
            </RequireSuperadmin>
          }
        />
        <Route path="merchants" element={<MerchantsPage />} />
        <Route
          path="administrators"
          element={
            <RequireSuperadmin>
              <AdministratorsPage />
            </RequireSuperadmin>
          }
        />
        <Route
          path="portfolios"
          element={
            <RequireSuperadmin>
              <PortfoliosPage />
            </RequireSuperadmin>
          }
        />
        <Route
          path="access"
          element={
            <RequireSuperadmin>
              <AccessPage />
            </RequireSuperadmin>
          }
        />
        <Route
          path="security"
          element={
            <RequireSuperadmin>
              <SecurityPage />
            </RequireSuperadmin>
          }
        />
        <Route path="account" element={<MyAccountPage />} />
        {/* The old "Platform roles" address (the screen that attached a role to an existing account is gone). */}
        <Route path="platform-roles" element={<Navigate to="/administrators" replace />} />
        <Route path="organisations" element={<ProvisionOrganisationPage />} />
        <Route path="analytics" element={<PlatformAnalyticsPage />} />
        {/* An address that no longer exists (the old /mfa among them) goes to the console, not to an empty frame. */}
        <Route path="*" element={<Home />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
